import * as React from 'react';
import { useState, useEffect } from 'react';
import { SharePointFolder } from '../../../types';
import { findNodeByPath, removeNodeAtPath } from '../../../utils/folderTree';
import FolderTree from './FolderTree';
import './SharePointFolderPicker.css';

export interface SharePointFolderPickerProps {
  folderTree: SharePointFolder[];
  selectedFolder: string;
  expandedPaths: Set<string>;
  sharepointFolderPath: string;
  documentsRootUrl?: string;
  isLoadingSharePoint: boolean;
  isRefreshingFolders: boolean;
  isProcessing: boolean;
  hasValidSite: boolean;
  onTreeSelect: (node: SharePointFolder) => void;
  onTreeToggle: (path: string) => void;
  onCreateFolder: (parentPath: string, folderName: string) => Promise<{ success: boolean; error?: string }>;
  onRenameFolder: (oldPath: string, newName: string) => Promise<{ success: boolean; error?: string }>;
  onDeleteFolder: (path: string) => Promise<{ success: boolean; error?: string }>;
  onMoveFolder: (sourcePath: string, newParentPath: string) => Promise<{ success: boolean; error?: string }>;
  onRefreshFolders: () => Promise<void> | void;
  onBusyChange?: (busy: boolean) => void;
}

const isValidFolderName = (name: string): boolean => {
  const trimmed = name.trim();
  if (trimmed.length === 0 || trimmed.length > 200) return false;
  if (/[<>:"|?*\\/]/.test(trimmed)) return false;
  if (trimmed === '.' || trimmed === '..') return false;
  return true;
};

const SharePointFolderPicker: React.FC<SharePointFolderPickerProps> = ({
  folderTree,
  selectedFolder,
  expandedPaths,
  sharepointFolderPath,
  documentsRootUrl,
  isLoadingSharePoint,
  isRefreshingFolders,
  isProcessing,
  hasValidSite,
  onTreeSelect,
  onTreeToggle,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveFolder,
  onRefreshFolders,
  onBusyChange,
}) => {
  const [pendingParent, setPendingParent] = useState<string | null>(null);
  const [pendingName, setPendingName] = useState<string>('');
  const [pendingError, setPendingError] = useState<string>('');
  const [isPendingCreating, setIsPendingCreating] = useState<boolean>(false);

  const [renamingPath, setRenamingPath] = useState<string | null>(null);
  const [renameName, setRenameName] = useState<string>('');
  const [renameError, setRenameError] = useState<string>('');
  const [isRenaming, setIsRenaming] = useState<boolean>(false);

  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; targetPath: string } | null>(null);
  const [copyToast, setCopyToast] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState<{ path: string; label: string } | null>(null);
  const [deleteError, setDeleteError] = useState<string>('');

  const [movingSource, setMovingSource] = useState<{ path: string; label: string } | null>(null);
  const [moveDestination, setMoveDestination] = useState<string>('');
  const [moveExpandedPaths, setMoveExpandedPaths] = useState<Set<string>>(new Set());
  const [moveError, setMoveError] = useState<string>('');
  const [isMoving, setIsMoving] = useState<boolean>(false);

  useEffect(() => {
    if (!onBusyChange) return;
    const busy =
      pendingParent !== null ||
      isPendingCreating ||
      renamingPath !== null ||
      isRenaming ||
      isDeleting ||
      confirmDelete !== null ||
      isMoving ||
      movingSource !== null;
    onBusyChange(busy);
  }, [
    onBusyChange,
    pendingParent,
    isPendingCreating,
    renamingPath,
    isRenaming,
    isDeleting,
    confirmDelete,
    isMoving,
    movingSource,
  ]);

  useEffect(() => {
    if (!contextMenu) return;
    const onClick = () => setContextMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null);
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  if (isLoadingSharePoint) {
    return (
      <div className="info-box">
        <div className="loading-spinner loading-spinner--small"></div>
        <p className="info-box-text">SharePoint mappen laden...</p>
      </div>
    );
  }

  if (!hasValidSite) {
    return (
      <div className="info-banner info-banner--sharepoint-warning">
        <span className="material-icons-round banner-icon">warning</span>
        <div className="banner-content">
          <p className="banner-text">Er is geen SharePoint-site beschikbaar voor deze klant.</p>
        </div>
      </div>
    );
  }

  const startInlineCreate = (parentPath: string) => {
    if (isProcessing || isPendingCreating) return;
    if (parentPath !== '' && !expandedPaths.has(parentPath)) {
      onTreeToggle(parentPath);
    }
    setPendingParent(parentPath);
    setPendingName('Nieuwe map');
    setPendingError('');
  };

  const cancelInlineCreate = () => {
    setPendingParent(null);
    setPendingName('');
    setPendingError('');
  };

  const submitInlineCreate = async () => {
    if (pendingParent === null || isPendingCreating) return;
    const trimmed = pendingName.trim();
    if (!isValidFolderName(trimmed)) {
      setPendingError('Niet toegestaan: / \\ < > : " | ? *');
      return;
    }
    setIsPendingCreating(true);
    setPendingError('');
    const result = await onCreateFolder(pendingParent, trimmed);
    setIsPendingCreating(false);
    if (result.success) {
      cancelInlineCreate();
    } else {
      setPendingError(result.error || 'Map kon niet worden aangemaakt.');
    }
  };

  const startRename = (path: string) => {
    if (!path || isProcessing || isPendingCreating || isRenaming) return;
    const node = findNodeByPath(folderTree, path);
    if (!node) return;
    setRenamingPath(path);
    setRenameName(node.name);
    setRenameError('');
  };

  const cancelRename = () => {
    setRenamingPath(null);
    setRenameName('');
    setRenameError('');
  };

  const submitRename = async () => {
    if (renamingPath === null || isRenaming) return;
    const trimmed = renameName.trim();
    const node = findNodeByPath(folderTree, renamingPath);
    if (node && trimmed === node.name) {
      cancelRename();
      return;
    }
    if (!isValidFolderName(trimmed)) {
      setRenameError('Niet toegestaan: / \\ < > : " | ? *');
      return;
    }
    setIsRenaming(true);
    setRenameError('');
    const result = await onRenameFolder(renamingPath, trimmed);
    setIsRenaming(false);
    if (result.success) {
      cancelRename();
    } else {
      setRenameError(result.error || 'Map kon niet worden hernoemd.');
    }
  };

  const copyUrlForPath = async (path: string) => {
    let url = '';
    if (path === '') {
      url = documentsRootUrl || '';
    } else {
      const node = findNodeByPath(folderTree, path);
      url = node?.webUrl || '';
    }
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopyToast('URL gekopieerd');
      setTimeout(() => setCopyToast(''), 1500);
    } catch {
      setCopyToast('Kopiëren mislukt');
      setTimeout(() => setCopyToast(''), 1500);
    }
  };

  const requestDelete = (path: string) => {
    if (!path || isDeleting) return;
    const node = findNodeByPath(folderTree, path);
    setDeleteError('');
    setConfirmDelete({ path, label: node?.name || path });
  };

  const cancelDelete = () => {
    if (isDeleting) return;
    setConfirmDelete(null);
    setDeleteError('');
  };

  const confirmDeleteSubmit = async () => {
    if (!confirmDelete || isDeleting) return;
    setIsDeleting(true);
    setDeleteError('');
    const result = await onDeleteFolder(confirmDelete.path);
    setIsDeleting(false);
    if (result.success) {
      setConfirmDelete(null);
    } else {
      setDeleteError(result.error || 'Map kon niet worden verwijderd.');
    }
  };

  const requestMove = (path: string) => {
    if (!path || isMoving) return;
    const node = findNodeByPath(folderTree, path);
    setMoveError('');
    setMoveDestination('');
    setMoveExpandedPaths(new Set());
    setMovingSource({ path, label: node?.name || path });
  };

  const cancelMove = () => {
    if (isMoving) return;
    setMovingSource(null);
    setMoveDestination('');
    setMoveError('');
  };

  const toggleMoveExpand = (path: string) => {
    setMoveExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const submitMove = async () => {
    if (!movingSource || isMoving) return;
    const source = movingSource.path;
    const dest = moveDestination;
    const sourceParent = source.split('/').slice(0, -1).join('/');
    if (dest === source || dest.startsWith(`${source}/`)) {
      setMoveError('Je kunt een map niet naar zichzelf of een submap verplaatsen.');
      return;
    }
    if (dest === sourceParent) {
      setMoveError('De map staat al in de geselecteerde bestemming.');
      return;
    }
    setIsMoving(true);
    setMoveError('');
    const result = await onMoveFolder(source, dest);
    setIsMoving(false);
    if (result.success) {
      setMovingSource(null);
      setMoveDestination('');
    } else {
      setMoveError(result.error || 'Map kon niet worden verplaatst.');
    }
  };

  const handleRowContextMenu = (e: React.MouseEvent, targetPath: string) => {
    e.preventDefault();
    if (isProcessing || isPendingCreating || isRenaming || isDeleting || isMoving) return;
    setContextMenu({ x: e.clientX, y: e.clientY, targetPath });
  };

  return (
    <div className="form-section">
      <div className="folder-section-header">
        <label className="form-label">SharePoint Map</label>
      </div>

      <FolderTree
        nodes={folderTree}
        selectedPath={selectedFolder}
        expandedPaths={expandedPaths}
        rootNode={{ name: 'Hoofdmap', path: '', webUrl: documentsRootUrl, children: [] }}
        disabled={isProcessing || isPendingCreating || isRenaming || isDeleting || isMoving || isRefreshingFolders}
        isRefreshing={isRefreshingFolders}
        onRefresh={onRefreshFolders}
        pendingParent={pendingParent}
        pendingName={pendingName}
        pendingError={pendingError}
        isPendingCreating={isPendingCreating}
        renamingPath={renamingPath}
        renameName={renameName}
        renameError={renameError}
        isRenaming={isRenaming}
        onSelect={onTreeSelect}
        onToggle={onTreeToggle}
        onPendingNameChange={setPendingName}
        onPendingSubmit={submitInlineCreate}
        onPendingCancel={cancelInlineCreate}
        onRenameNameChange={setRenameName}
        onRenameSubmit={submitRename}
        onRenameCancel={cancelRename}
        onRowContextMenu={handleRowContextMenu}
      />

      {contextMenu && (() => {
        const target = contextMenu.targetPath;
        const node = target === '' ? null : findNodeByPath(folderTree, target);
        const targetUrl = target === '' ? documentsRootUrl || '' : node?.webUrl || '';
        const canCopy = !!targetUrl;
        const canOpen = !!targetUrl;
        const isRootLevel = target !== '' && !target.includes('/');
        const canRename = target !== '' && !isRootLevel;
        const canMove = target !== '' && !isRootLevel;
        const canDelete = target !== '' && !isRootLevel;
        return (
          <div
            className="folder-context-menu"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          >
            <button
              type="button"
              className="folder-context-menu-item"
              onClick={() => {
                const parent = contextMenu.targetPath;
                setContextMenu(null);
                startInlineCreate(parent);
              }}
            >
              <span className="material-icons-round">create_new_folder</span>
              Nieuwe map
            </button>
            <button
              type="button"
              className="folder-context-menu-item"
              disabled={!canRename}
              title={isRootLevel ? 'Hoofdmappen kunnen niet worden hernoemd' : undefined}
              onClick={() => {
                const path = contextMenu.targetPath;
                setContextMenu(null);
                startRename(path);
              }}
            >
              <span className="material-icons-round">drive_file_rename_outline</span>
              Naam wijzigen
            </button>
            <button
              type="button"
              className="folder-context-menu-item"
              disabled={!canMove}
              title={isRootLevel ? 'Hoofdmappen kunnen niet worden verplaatst' : undefined}
              onClick={() => {
                const path = contextMenu.targetPath;
                setContextMenu(null);
                requestMove(path);
              }}
            >
              <span className="material-icons-round">drive_file_move</span>
              Verplaatsen
            </button>
            <button
              type="button"
              className="folder-context-menu-item"
              disabled={!canCopy}
              onClick={() => {
                const path = contextMenu.targetPath;
                setContextMenu(null);
                copyUrlForPath(path);
              }}
            >
              <span className="material-icons-round">content_copy</span>
              Kopieer URL
            </button>
            <button
              type="button"
              className="folder-context-menu-item"
              disabled={!canOpen}
              onClick={() => {
                setContextMenu(null);
                window.open(targetUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              <span className="material-icons-round">open_in_new</span>
              Open in nieuw tabblad
            </button>
            <button
              type="button"
              className="folder-context-menu-item folder-context-menu-item--danger"
              disabled={!canDelete}
              title={isRootLevel ? 'Hoofdmappen kunnen niet worden verwijderd' : undefined}
              onClick={() => {
                const path = contextMenu.targetPath;
                setContextMenu(null);
                requestDelete(path);
              }}
            >
              <span className="material-icons-round">delete</span>
              Verwijderen
            </button>
          </div>
        );
      })()}

      {copyToast && <div className="folder-copy-toast">{copyToast}</div>}

      {confirmDelete && (
        <div
          className="folder-confirm-overlay"
          onClick={cancelDelete}
        >
          <div
            className="folder-confirm-dialog"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="folder-confirm-title">
              <span className="material-icons-round folder-confirm-icon">warning</span>
              Map verwijderen
            </div>
            <p className="folder-confirm-body">
              Weet je zeker dat je <strong>{confirmDelete.label}</strong> en alle
              inhoud wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </p>
            {deleteError && (
              <p className="folder-confirm-error">{deleteError}</p>
            )}
            <div className="folder-confirm-actions">
              <button
                type="button"
                className="folder-confirm-btn"
                disabled={isDeleting}
                onClick={cancelDelete}
              >
                Annuleer
              </button>
              <button
                type="button"
                className="folder-confirm-btn folder-confirm-btn--danger"
                disabled={isDeleting}
                onClick={confirmDeleteSubmit}
              >
                {isDeleting ? 'Bezig...' : 'Verwijder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {movingSource && (() => {
        const pickerTree = removeNodeAtPath(folderTree, movingSource.path);
        const sourceParent = movingSource.path.split('/').slice(0, -1).join('/');
        const isSameAsCurrentParent = moveDestination === sourceParent;
        const destLabel = moveDestination === ''
          ? 'Hoofdmap'
          : findNodeByPath(pickerTree, moveDestination)?.name || moveDestination;
        return (
          <div className="folder-confirm-overlay" onClick={cancelMove}>
            <div
              className="folder-confirm-dialog folder-move-dialog"
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="folder-confirm-title">
                <span className="material-icons-round folder-move-icon">drive_file_move</span>
                Map verplaatsen
              </div>
              <p className="folder-confirm-body">
                Kies een nieuwe bestemming voor <strong>{movingSource.label}</strong>.
              </p>
              <div className="folder-move-tree-wrap">
                <FolderTree
                  nodes={pickerTree}
                  selectedPath={moveDestination}
                  expandedPaths={moveExpandedPaths}
                  rootNode={{ name: 'Hoofdmap', path: '', webUrl: documentsRootUrl, children: [] }}
                  disabled={isMoving}
                  onSelect={(node) => setMoveDestination(node.path)}
                  onToggle={toggleMoveExpand}
                />
              </div>
              <p className="folder-move-summary">
                Bestemming: <strong>{destLabel}</strong>
                {isSameAsCurrentParent && (
                  <span className="folder-move-summary-hint"> (huidige locatie)</span>
                )}
              </p>
              {moveError && <p className="folder-confirm-error">{moveError}</p>}
              <div className="folder-confirm-actions">
                <button
                  type="button"
                  className="folder-confirm-btn"
                  disabled={isMoving}
                  onClick={cancelMove}
                >
                  Annuleer
                </button>
                <button
                  type="button"
                  className="folder-confirm-btn folder-confirm-btn--primary"
                  disabled={isMoving || isSameAsCurrentParent}
                  onClick={submitMove}
                >
                  {isMoving ? 'Bezig...' : 'Verplaats'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {sharepointFolderPath && (
        <div className="sharepoint-folder-card">
          <span className="sharepoint-folder-card-label">Geselecteerde map</span>
          <a
            href={sharepointFolderPath}
            target="_blank"
            rel="noopener noreferrer"
            className="sharepoint-folder-card-link"
            title={sharepointFolderPath}
          >
            <span className="sharepoint-folder-card-url">{sharepointFolderPath}</span>
            <span className="material-icons-round sharepoint-folder-card-icon">open_in_new</span>
          </a>
        </div>
      )}
    </div>
  );
};

export default SharePointFolderPicker;
